import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createReport, getOrganisations, getSites, createSite, getScopeOfAssessmentTypes, getOrganisationMembers, getOrgLicenceSummary } from '../api';
import { useAuth } from '../context/AuthContext';
import Breadcrumb from '../components/Breadcrumb';

function SitePickerModal({ isOpen, onClose, onSelect, sites, loading, organisationId, onSitesChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addAddress, setAddAddress] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setShowAdd(false);
      setAddName('');
      setAddAddress('');
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    setError('');
    try {
      const newSite = await createSite({ organisationId: Number(organisationId), name: addName.trim(), address: addAddress.trim() || null });
      setAddName('');
      setAddAddress('');
      setShowAdd(false);
      onSitesChanged();
      onSelect(newSite);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div className="location-modal" onClick={(e) => e.stopPropagation()}>
        <div className="location-modal-header">
          <h3>Select Site</h3>
          <button className="location-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="location-modal-body" style={showAdd && !loading && sites.length === 0 ? { minHeight: 0 } : undefined}>
          {loading && <p style={{ padding: '1rem', color: '#888' }}>Loading sites...</p>}
          {!loading && sites.length === 0 && !showAdd && <p style={{ padding: '1rem', color: '#888' }}>No sites found for this organisation.</p>}
          {!loading && sites.map((site) => (
            <div
              key={site.id}
              className="location-tree-node"
              style={{ paddingLeft: 12 }}
              onClick={() => { onSelect(site); onClose(); }}
            >
              <span className="location-tree-name">{site.name}</span>
              {site.address && <span className="location-tree-type">{site.address}</span>}
            </div>
          ))}
        </div>

        <div className="location-add-section">
          {!showAdd ? (
            <button className="btn-link" onClick={() => setShowAdd(true)} style={{ padding: '0.5rem 0' }}>+ Add New Site</button>
          ) : (
            <div>
              <input
                placeholder="Site name"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.95rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
              <textarea
                placeholder="Address"
                value={addAddress}
                onChange={(e) => setAddAddress(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.3rem 0' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }} onClick={handleAdd} disabled={adding}>
                  {adding ? 'Adding...' : 'Add'}
                </button>
                <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', color: '#333', borderColor: '#ccc' }} onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CreateReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitePickerOpen, setSitePickerOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [scopeTypes, setScopeTypes] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    organisationId: '',
    siteId: '',
    assessorUserId: user?.userId || '',
    assessmentDate: new Date().toISOString().split('T')[0],
    reviewDate: '',
    scopeOfAssessment: '',
    preparedFor: '',
    siteName: '',
    siteAddress: '',
  });

  useEffect(() => {
    getOrganisations().then(setOrgs).catch(() => {});
    getScopeOfAssessmentTypes().then(setScopeTypes).catch(() => {});
  }, []);

  const loadSites = useCallback(() => {
    if (!form.organisationId) {
      setSites([]);
      return;
    }
    setSitesLoading(true);
    getSites(form.organisationId)
      .then(setSites)
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, [form.organisationId]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (!form.organisationId) {
      setMembers([]);
      setCreditSummary(null);
      return;
    }
    getOrgLicenceSummary(form.organisationId).then(setCreditSummary).catch(() => setCreditSummary(null));
    getOrganisationMembers(form.organisationId)
      .then((m) => {
        setMembers(m);
        const currentUserIsMember = m.some((u) => u.userId === user?.userId);
        if (!currentUserIsMember && m.length > 0) {
          setForm((prev) => ({ ...prev, assessorUserId: m[0].userId }));
        }
      })
      .catch(() => setMembers([]));
  }, [form.organisationId, user?.userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'organisationId') {
      setForm({ ...form, organisationId: value, siteId: '', siteName: '', siteAddress: '' });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSelectSite = (site) => {
    setForm((prev) => ({
      ...prev,
      siteId: String(site.id),
      siteName: site.name,
      siteAddress: site.address || '',
    }));
  };

  const handleClearSite = () => {
    setForm((prev) => ({ ...prev, siteId: '', siteName: '', siteAddress: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.siteId) {
      setError('Please select a site');
      return;
    }
    if (form.reviewDate && form.assessmentDate && form.reviewDate < form.assessmentDate) {
      setError('Review date must be on or after assessment date');
      return;
    }
    try {
      const report = await createReport({
        ...form,
        organisationId: Number(form.organisationId),
        siteId: Number(form.siteId),
        assessorUserId: Number(form.assessorUserId),
      });
      navigate(`/reports/${report.id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const selectedSite = sites.find((s) => String(s.id) === form.siteId);

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Reports', to: '/reports' },
          { label: 'New Report' },
        ]} />
        <h2>New Report</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Organisation</label>
            <select name="organisationId" value={form.organisationId} onChange={handleChange} required>
              <option value="">Select...</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Site</label>
            {selectedSite ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6 }}>
                <div style={{ flex: 1 }}>
                  <strong>{selectedSite.name}</strong>
                  {selectedSite.address && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.85rem' }}>{selectedSite.address}</span>}
                </div>
                <button type="button" className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => setSitePickerOpen(true)}>Change</button>
                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1.1rem', padding: '0 4px' }} onClick={handleClearSite} title="Clear site">&times;</button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                onClick={() => setSitePickerOpen(true)}
                disabled={!form.organisationId}
              >
                Select Site
              </button>
            )}
          </div>
          <div className="form-group">
            <label>Report Author</label>
            <select name="assessorUserId" value={form.assessorUserId} onChange={handleChange} required>
              {!form.organisationId && <option value={user?.userId || ''}>{user?.firstName} {user?.lastName} (you)</option>}
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.firstName} {m.lastName}{m.userId === user?.userId ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Prepared For</label>
            <input type="text" name="preparedFor" value={form.preparedFor} onChange={handleChange} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Assessment Date</label>
              <input type="date" name="assessmentDate" value={form.assessmentDate} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Review Date</label>
              <input type="date" name="reviewDate" value={form.reviewDate} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Scope of Assessment</label>
            <div className="checkbox-group">
              {scopeTypes.map((st) => (
                <label key={st.code}>
                  <input
                    type="radio"
                    name="scopeOfAssessment"
                    value={st.code}
                    checked={form.scopeOfAssessment === st.code}
                    onChange={handleChange}
                  />
                  {st.label}
                </label>
              ))}
            </div>
          </div>
          {creditSummary && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              borderRadius: '6px',
              background: creditSummary.creditsRemaining > 0 ? '#e8f5e9' : '#fce4ec',
              border: `1px solid ${creditSummary.creditsRemaining > 0 ? '#a5d6a7' : '#ef9a9a'}`,
            }}>
              {creditSummary.creditsRemaining > 0
                ? `${creditSummary.creditsRemaining} report credit${creditSummary.creditsRemaining !== 1 ? 's' : ''} remaining`
                : 'No report credits remaining. Please redeem a licence before creating a report.'}
            </div>
          )}
          {creditSummary && creditSummary.creditsRemaining <= 0 && (
            <div className="alert alert-error">Cannot create report — no credits remaining for this organisation.</div>
          )}
          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={!form.siteId || !form.scopeOfAssessment || (creditSummary && creditSummary.creditsRemaining <= 0)}
          >Create Report</button>
        </form>
      </div>

      <SitePickerModal
        isOpen={sitePickerOpen}
        onClose={() => setSitePickerOpen(false)}
        onSelect={handleSelectSite}
        sites={sites}
        loading={sitesLoading}
        organisationId={form.organisationId}
        onSitesChanged={loadSites}
      />
    </div>
  );
}
